import { Component, inject, Inject, ViewChild } from '@angular/core';
import { ModalComponent } from '../../../../../../../../shared/components/modals/modal/modal.component';
import { TranslatePipe } from '../../../../../../../../shared/pipes/translate/translate.pipe';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { GridComponent } from '../../../../../../../../shared/components/grid/grid.component';
import { GridOptions } from '../../../../../../../../shared/classes/list/grid-options';
import { GridOptionsDto } from '../../../../../../../../../models/ts/grid-options-dto.model';
import { GridColumnBase } from '../../../../../../../../shared/classes/list/grid-column-base';
import { GridColumnType } from '../../../../../../../../../models/ts/grid-column-type.model';
import { QuestionGroup } from '../../../../../../../../../models/ts/question-group.model';
import { CollectionListApiService } from '../../../../../../../../api/bizzmine/collection-list/collection-list-api.service';
import { map, switchMap, take, tap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { userSettingsFeature } from '../../../../../../../../store/features/user-settings/user-settings-feature';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { CollectionFieldListOptionsConfiguration } from '../../../../../../../../../models/ts/collection-form-field-value.model';
import { IconComponent } from '../../../../../../../../shared/components/ui/icon/icon.component';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { DropTargetEvent } from '@progress/kendo-angular-utils';
import { calculateDestinationIndex } from '../../../../../../../../shared/components/grid/helpers/calculate-destination-index';
import { orderBy } from '@progress/kendo-data-query';
import { LoaderComponent } from 'src/app/shared/components/ui/loader/loader.component';

@Component({
  selector: 'bizz-checklists-group-sort-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    GridComponent,
    GridComponent,
    FormsModule,
    ComboBoxComponent,
    IconComponent,
    TooltipDirective,
    LoaderComponent
  ],
  templateUrl: './checklists-group-sort-modal.component.html',
  styleUrl: './checklists-group-sort-modal.component.scss'
})
export class ChecklistsGroupSortModalComponent {
  public groups: QuestionGroup[] = [];

  public gridOptions: GridOptions;
  public questionGroups: QuestionGroup[];
  public currentPage: number = 1;
  private collectionListApiService = inject(CollectionListApiService);
  private store$ = inject(Store);
  public languageOptions: Array<{ Value: number, Text: string }>;
  public selectedLanguageId: number;
  public systemLanguageId: number = this.store$.selectSignal(userSettingsFeature.selectCompanyLanguagesID)();
  public rowReordering: boolean = true;
  public loading: boolean = true;

  @ViewChild('gridComponent', { static: false })
  public gridComponent: GridComponent;

  public groups$ = this.collectionListApiService.getCollectionFieldListOptionsConfiguration({
    CollectionId: this.data.collectionId,
    CollectionFieldValues: this.data.collectionFieldValues
  }).pipe(
    tap(data => {
      if (data && data.length > 0) {
        this.languageOptions = data[0].LanguageConfigurations.map(l => {
          return { Value: l.LanguageId, Text: l.LanguageName };
        });
      }
    }),
    map((data) => {
      data.forEach(d => {
        let placeHolder = d.LanguageConfigurations.find(_ => _.LanguageId == this.systemLanguageId)?.Caption;
        if (placeHolder == undefined || placeHolder.trim() == '') {
          placeHolder = d.LanguageConfigurations.find(_ => _.Caption != undefined && _.Caption.trim() != '')?.Caption;
        }
        d.LanguageConfigurations.forEach(l => {
          l.placeHolder = placeHolder ?? '';
        });
      });
      this.loading = false;
      return data;
    })
  );

  public gridData: Array<CollectionFieldListOptionsConfigurationRow> = [];

  public onLanguageChange(languageId: number): void {
    this.selectedLanguageId = languageId;
    this.gridOptions = structuredClone(this.gridOptions);
  }

  public constructor(public dialogRef: DialogRef<any>, @Inject(DIALOG_DATA) public data: {
    questionGroups: QuestionGroup[],
    collectionId: number,
    collectionFieldValues: Array<number>;
  }) {
    this.groups$.pipe(take(1)).subscribe(data => {
      this.gridData = orderBy(data as Array<CollectionFieldListOptionsConfigurationRow>, [{
        field: 'IndexPosition',
        dir: 'asc'
      }]);
    });
    this.selectedLanguageId = this.store$.selectSignal(userSettingsFeature.selectUserLanguagesID)();
    const gridOptionsDto: GridOptionsDto = {
      columns: [],
      dataSource: { group: [], sort: [] },
      groupable: { messages: { empty: '' } },
      name: '',
      pageable: {
        alwaysVisible: false,
        info: false,
        numeric: false,
        pageSizes: false,
        previousNext: false,
        refresh: false
      },
      reorderable: false,
      resizable: false,
      scrollable: false,
      selectable: false,
      sortable: { mode: 'single', allowUnsort: false, showIndexes: false }
    };
    this.gridOptions = new GridOptions(gridOptionsDto);
    this.gridOptions.groupable.enabled = false;
    this.gridOptions.sortable = false;
    this.gridOptions.columns = [
      {
        title: 'Group Name',
        GridColumnType: GridColumnType.Text
      }
    ] as GridColumnBase[];
    this.questionGroups = data.questionGroups;
  }

  public save(): void {
    this.collectionListApiService.saveCollectionFieldListOptionsConfiguration(this.data.collectionId, this.gridData)
      .pipe(take(1),
        //Refetch ALL the groups of the collection (not just the updated ones).
        switchMap(() => this.collectionListApiService.getCollectionFieldListOptionsConfiguration({
          CollectionId: this.data.collectionId
        })))
      .subscribe({
        next: (updatedGroups: CollectionFieldListOptionsConfiguration[]) => {
          this.dialogRef.close(updatedGroups);
        }
      });
  }

  public editInlineClicked(data: any): void {
    data.editMode = true;
    this.rowReordering = false;
  }

  public saveClicked(data: any): void {
    data.editMode = false;

    this.rowReordering = !this.gridData.some(
      (row) => row.editMode);
    this.gridData = orderBy(this.gridData, [{
      field: 'IndexPosition',
      dir: 'asc'
    }]);
  }

  public onRowDrop(e: DropTargetEvent): void {
    if (e && e.dropTargetIndex != undefined && e.dragData != undefined) {
      const fromIndex = e.dragData;
      const toIndex = calculateDestinationIndex(e, 0, fromIndex, 0);
      const draggedRow = this.gridData[fromIndex];
      this.gridData.splice(fromIndex, 1);
      this.gridData.splice(toIndex, 0, draggedRow);
      this.gridData.forEach((row, index) => row.IndexPosition = index + 1);
    }
  }
}

export interface CollectionFieldListOptionsConfigurationRow extends CollectionFieldListOptionsConfiguration {
  editMode: boolean;
}
